/**
 * Rate limiting utility for client-side protection
 * Note: Real rate limiting should be implemented server-side in edge functions
 * This provides basic client-side throttling
 */

interface RateLimitStore {
  [key: string]: {
    count: number;
    resetTime: number;
  };
}

const store: RateLimitStore = {};

/**
 * Check if action is allowed based on rate limit
 * @param key - Unique identifier for the rate limit (e.g., "otp:phone:+1234567890")
 * @param maxRequests - Maximum number of requests allowed
 * @param windowMs - Time window in milliseconds
 * @returns true if allowed, false if rate limited
 */
export function checkRateLimit(
  key: string,
  maxRequests: number,
  windowMs: number
): boolean {
  const now = Date.now();
  const entry = store[key];
  
  // If no entry or window expired, reset
  if (!entry || now > entry.resetTime) {
    store[key] = {
      count: 1,
      resetTime: now + windowMs,
    };
    return true;
  }
  
  // Check if limit exceeded
  if (entry.count >= maxRequests) {
    return false;
  }
  
  // Increment count
  entry.count++;
  return true;
}

/**
 * Get remaining requests for a rate limit key
 */
export function getRemainingRequests(
  key: string,
  maxRequests: number
): number {
  const entry = store[key];
  if (!entry) {
    return maxRequests;
  }
  
  return Math.max(0, maxRequests - entry.count);
}

/**
 * Get time until rate limit resets (in seconds)
 */
export function getResetTime(key: string): number {
  const entry = store[key];
  if (!entry) {
    return 0;
  }
  
  const remaining = Math.max(0, entry.resetTime - Date.now());
  return Math.ceil(remaining / 1000);
}

/**
 * Clear rate limit for a key (useful for testing or manual reset)
 */
export function clearRateLimit(key: string): void {
  delete store[key];
}

/**
 * Rate limit configurations
 */
export const RATE_LIMITS = {
  OTP_SEND: {
    maxRequests: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
  },
  LOGIN_ATTEMPT: {
    maxRequests: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
  },
  BOOKING_CREATE: {
    maxRequests: 10,
    windowMs: 60 * 60 * 1000, // 1 hour
  },
  PROMO_CODE_VALIDATE: {
    maxRequests: 20,
    windowMs: 60 * 1000, // 1 minute
  },
} as const;

/**
 * Check OTP send rate limit
 */
export function checkOtpRateLimit(phone: string): boolean {
  const key = `otp:${phone}`;
  return checkRateLimit(
    key,
    RATE_LIMITS.OTP_SEND.maxRequests,
    RATE_LIMITS.OTP_SEND.windowMs
  );
}

/**
 * Check login attempt rate limit
 */
export function checkLoginRateLimit(identifier: string): boolean {
  const key = `login:${identifier}`;
  return checkRateLimit(
    key,
    RATE_LIMITS.LOGIN_ATTEMPT.maxRequests,
    RATE_LIMITS.LOGIN_ATTEMPT.windowMs
  );
}

/**
 * Check booking creation rate limit
 */
export function checkBookingRateLimit(userId?: string): boolean {
  const key = userId ? `booking:${userId}` : `booking:anonymous`;
  return checkRateLimit(
    key,
    RATE_LIMITS.BOOKING_CREATE.maxRequests,
    RATE_LIMITS.BOOKING_CREATE.windowMs
  );
}

/**
 * Check promo code validation rate limit
 */
export function checkPromoCodeRateLimit(): boolean {
  const key = `promo:${Date.now()}`; // Per-request key for IP-based limiting
  return checkRateLimit(
    key,
    RATE_LIMITS.PROMO_CODE_VALIDATE.maxRequests,
    RATE_LIMITS.PROMO_CODE_VALIDATE.windowMs
  );
}

